import React, { useState, useEffect } from 'react';
import { openPdfInNewWindow } from '../utils/pdfHandler';
import { getCollection } from '../utils/dataService';

/*
Bond Revocation and Jail Timeline Summary

Initial Incident (Not an Arrest): April 29, 2023
- Voluntary hospital visit after car accident; no police involvement until discharge.
- Arrested upon hospital discharge; initial bond $3,000 (posted May 3, 2023).
- Evidence: Transcript of call with Shenandoah Police (Doc #75), Memorial Hermann Ambulatory Summary (Doc #67), Surety Bond ($3,000, Doc #3).

First Bond Revocation: Aug 2, 3 - Aug 31, 2023
- Bond set aside for failure to report, missed urinalysis, unpaid fees.
- New bond $6,000 (Aug 31, 2023); released Sep 6, 2023.
- Evidence: Order Setting Aside Bond (Aug 2, 2023, Doc #8), Order to Reinstate/Set Bond (Aug 31, 2023, Doc #13), Surety Bond ($6,000, Doc #15).

Second Bond Revocation: Sep 28, 2023 - Oct 8, 2023
- Bond set aside for failed urinalysis, ignition interlock issues.
- New bond $25,000 (Oct 8, 2023); released Oct 14, 2023.
- Evidence: Order Setting Aside Bond (Sep 28, 2023, Doc #18), Order Setting Bond ($25,000, Oct 8, 2023, Doc #44), Felony Bond ($25,000, Doc #45).

Third Bond Revocation: Feb 15, 2024 - Mar 26, 2024
- Bond set aside for alleged methamphetamine use (prescribed Adderall), missed tests, interlock issues.
- New bond $10,000 (Mar 26, 2024); released Apr 12, 4.
- Evidence: Order Setting Aside Bond (Feb 15, 2024, Doc #26), Order Setting Bond ($10,000, Mar 26, 2024, Doc #31), Felony Bond ($10,000, Doc #32).

Fourth Bond Revocation: Oct 29, 2024 - Nov 21, 2024
- Bond set aside for missed urinalysis tests.
- New bond $5,000 (Nov 21, 2024); posted Nov 22, 2024.
- Evidence: Order Setting Aside Bond (Oct 29, 2024, Doc #46), Order Setting Bond ($15,000, Nov 21, 2024, Doc #22), Surety Bond (Nov 22, 2024, Doc #23).

Fifth Bond Issue: May 13, 2024 - Bond Forfeiture
- Failed to appear at court hearing.
- Bond $15,000 (Aug 27, 2024); posted Aug 28, 2024.
- Evidence: Bailiff's Certificate (May 15, 2024, Doc #33), Order Setting Bond ($15,000, Aug 27, 2024, Doc #38), Felony Bond (Aug 28, 4, Doc #39).

Sixth Bond Issue: Nov 25, 2024
- Bailiff's Certificate for Bond Forfeiture for failure to appear.
- You were at VA hospital (confirmed by VA appointment letter).
- Bond amount unclear.
- Evidence: Bailiff's Certificate (Nov 25, 2024, Doc #55), VA appointment letter (Nov 25, 2024, Doc #56).

Total approximate days incarcerated due to bond issues: 102 days
*/

// Hardcoded timeline data with all events
const HARDCODED_TIMELINE = [
  {
    id: 1,
    date: "April 28, 2023",
    time: "07:00 AM",
    title: "Morning Activities Begin",
    description: "Started day at residence (550 Hermitage Ct. Conroe, TX 77302) with normal morning routine. Prepared and consumed standard breakfast (eggs, toast, coffee) demonstrating typical daily functions and normal cognitive abilities.",
    type: "daily_activity",
    category: "pre_incident",
    significance: "Establishes baseline normal behavior and cognitive function approximately 19 hours before incident",
    location: {
      address: "550 Hermitage Ct.",
      city: "Conroe",
      state: "TX",
      zip: "77302"
    },
    attachments: []
  },
  {
    id: 2,
    date: "April 28, 2023",
    time: "08:00 AM",
    title: "Professional Job Interview Communication",
    description: "Correspondence with Tito Flaque, Senior Talent Acquisition Professional at A-LIGN, regarding cybersecurity position offering $125,000+ salary. Communication demonstrates professional capacity and cognitive function.",
    type: "professional_activity",
    category: "pre_incident",
    significance: "Documents professional engagement and normal cognitive function approximately 18 hours before incident; establishes pending career opportunity later impacted by case",
    related_events: [7],
    communications: [
      {
        with: "Tito Flaque",
        company: "A-LIGN",
        topic: "Senior Penetration Tester position"
      }
    ],
    attachments: [
      { name: "A-LIGN Interview Confirmation.pdf", path: "/evidence/exhibits/20230428_EXHIBIT-D16_A-LIGN-Interview-Confirmation.pdf", type: "pdf", exhibitId: "D16" }
    ]
  },
  {
    id: 3,
    date: "April 28, 2023",
    time: "11:19 AM",
    title: "Amazon Prime Membership Activation",
    description: "Activated Amazon Prime membership and received confirmation email, demonstrating normal digital literacy, executive function, and online purchasing ability approximately 15 hours before incident.",
    type: "digital_activity",
    category: "pre_incident",
    significance: "Documents cognitive capacity to complete multi-step online registration and purchasing process; timestamp from Amazon servers provides verified activity record",
    digital_evidence: {
      platform: "Amazon",
      account: "landonmayo722@gmail.com",
      activity: "Prime membership activation",
      server_timestamp: "11:19 AM EDT"
    },
    attachments: [
      { name: "Amazon Prime Membership Confirmation.pdf", path: "/evidence/exhibits/20230428_EXHIBIT-D21_Amazon-Prime-Membership-Activated.pdf", type: "pdf", exhibitId: "D21" }
    ]
  },
  {
    id: 4,
    date: "April 28, 2023",
    time: "11:30 AM",
    title: "Professional Certification Study Session",
    description: "Conducted focused study for Certified Information Systems Security Professional (CISSP) examination for 75 minutes, demonstrating sustained concentration, technical comprehension, and professional development activities.",
    type: "professional_development",
    category: "pre_incident",
    significance: "Documents advanced cognitive functioning and technical comprehension skills approximately 14.5 hours before incident; establishes ongoing professional development in cybersecurity field",
    duration: "75 minutes",
    skills_demonstrated: [
      "Sustained focus",
      "Complex information processing",
      "Technical comprehension",
      "Strategic career development"
    ],
    related_events: [2, 7],
    attachments: []
  },
  {
    id: 5,
    date: "April 28, 2023",
    time: "13:00",
    title: "Lunch at Public Restaurant",
    description: "Had lunch at McDonald's restaurant in public setting, ordered complete meal (Big Mac, large Coke, fries) and conducted normal financial transaction. Restaurant location includes security cameras that would have recorded presence and behavior.",
    type: "public_activity",
    category: "pre_incident",
    significance: "Documents normal public behavior, appetite, and social functioning approximately 13 hours before incident; establishes presence at public location with potential surveillance footage",
    location: {
      name: "McDonald's",
      address: "8001 TX-242",
      city: "Conroe",
      state: "TX",
      zip: "77385"
    },
    potential_witnesses: ["Restaurant staff", "Other patrons", "Surveillance footage"],
    attachments: []
  },
  {
    id: 6,
    date: "April 28, 2023",
    time: "14:00",
    title: "Limited Alcohol Consumption at Residence",
    description: "Consumed one 12oz Karbach Love Street beer (4.8% ABV) at residence. Attempted but discarded Sol Clamato after three sips due to dislike of taste. Consumed microwavable bean burrito as afternoon snack.",
    type: "personal_activity",
    category: "pre_incident",
    significance: "Documents minimal and documented alcohol consumption approximately 12 hours before incident, well outside biological detection window. Demonstrates food consumption and normal taste discrimination.",
    consumption: {
      alcoholic: [
        {
          item: "Karbach Love Street IPA",
          quantity: "one 12oz bottle",
          ABV: "4.8%",
          completed: true
        },
        {
          item: "Sol Clamato",
          quantity: "three sips only",
          completed: false,
          notes: "Discarded due to taste preference"
        }
      ],
      food: [
        {
          item: "Bean burrito",
          preparation: "Microwaved"
        }
      ]
    },
    metabolic_timeline: "Single beer would be fully metabolized within 2-3 hours; approximately 9-10 hours before alleged incident",
    location: {
      type: "Private residence",
      address: "550 Hermitage Ct.",
      city: "Conroe",
      state: "TX"
    },
    attachments: []
  },
  {
    id: 7,
    date: "April 28, 2023",
    time: "15:30",
    title: "A-LIGN Senior Penetration Tester Job Interview",
    description: "Professional video interview with Tito Flaque, Senior Talent Acquisition Professional at A-LIGN, regarding Senior Penetration Tester position with $125,000-$134,300 compensation package. Interview demonstrates professional communication skills, technical knowledge, and career advancement.",
    type: "professional_interview",
    category: "pre_incident",
    significance: "Documents high-level cognitive functioning and professional capabilities approximately 10.5 hours before incident. Corporate records provide third-party verification of mental state and capabilities.",
    professional_details: {
      company: "A-LIGN",
      position: "Senior Penetration Tester Consultant",
      interviewer: "Tito Flaque",
      salary_offered: "$125,000 base with $5,000-$7,500 bonus and $150/month technology stipend",
      opportunity_value: "$131,800-$134,300 annual compensation",
      job_type: "100% Remote"
    },
    related_events: [2],
    attachments: [
      { name: "A-LIGN Job Offer Email.pdf", path: "/evidence/exhibits/20230428_EXHIBIT-D22_A-LIGN-Job-Offer-Email.pdf", type: "pdf", exhibitId: "D22" }
    ]
  },
  {
    id: 8,
    date: "April 28, 2023",
    time: "16:04",
    title: "Arizona State Government Cybersecurity Contract Offer",
    description: "Received formal job offer email from Eva Watson at E TalentNetwork for Cyber Security Project Manager position with State of Arizona at $87/hr contract rate ($181,000/year). Role involves leading penetration testing projects using advanced security tools (Nessus, Burp Suite) and Red Team techniques.",
    type: "professional_opportunity",
    category: "pre_incident",
    significance: "Documents high-level professional engagement with government client approximately 10 hours before incident. Email timestamp provides server-verified evidence of cognitive function and professional capabilities.",
    professional_details: {
      client: "State of Arizona via 22nd Century",
      contact: "Eva Watson",
      contact_role: "E TalentNetwork Recruiter",
      position_title: "Cyber Security Project Manager (23-07746)",
      location: "Phoenix, AZ, United States",
      compensation: "$87/hr on C2C basis ($181,000/year)",
      email_timestamp: "Fri, Apr 28, 2023 at 4:04 PM",
      contact_phone: "(571) 367-3207",
      contact_email: "evaw@etalentnetwork.com"
    },
    skills_required: [
      "Penetration testing with Red Team techniques",
      "Nessus and Burp Suite expertise",
      "Security testing and vulnerability analysis",
      "Technical reporting and stakeholder communication",
      "Cloud infrastructure security assessment",
      "Application and network security testing"
    ],
    verification_sources: [
      "Email server timestamps",
      "E TalentNetwork corporate records",
      "State of Arizona procurement records"
    ],
    attachments: [
      { name: "State of AZ Job Offer Email.pdf", path: "/evidence/exhibits/20230428_EXHIBIT-D8_State-of-AZ-Job-Offer.pdf", type: "pdf", exhibitId: "D8" }
    ]
  },
  {
    id: 9,
    date: "April 28, 2023",
    time: "16:15",
    title: "Beginning of Evening Transportation Activities",
    description: "Departed residence to retrieve Honda Civic from Maaco auto body shop in Spring, TX. Trip initiated transportation sequence leading to later vehicle usage.",
    type: "vehicle_related",
    category: "pre_incident",
    significance: "Documents start of vehicle-related activities approximately 10 hours before incident. Establishes timeline of automobile possession and usage.",
    transportation_details: {
      starting_location: "550 Hermitage Ct., Conroe, TX",
      destination: "Maaco auto body shop, Spring, TX",
      purpose: "Vehicle pickup",
      vehicle_details: {
        make: "Honda",
        model: "Civic",
        status: "Recently serviced/repaired",
        location: "Maaco auto body shop"
      }
    },
    related_events: [10, 11],
    verification_potential: "Maaco service records, security footage, payment receipts",
    attachments: []
  },
  {
    id: 10,
    date: "April 28, 2023",
    time: "17:00",
    title: "Secondary Vehicle Storage at Commercial Facility",
    description: "Drove Honda Pilot to U-Haul self-storage facility (1609 Spring Cypress Rd, Spring, TX 77388) and secured it for temporary storage, demonstrating normal vehicle operation and physical capabilities.",
    type: "vehicle_management",
    category: "pre_incident",
    significance: "Documents proper vehicle operation and deliberate vehicle management decisions approximately 9 hours before incident. Establishes secondary vehicle was properly stored, not abandoned.",
    transportation_details: {
      starting_location: "Unknown previous location",
      destination: "U-Haul self-storage facility",
      address: "1609 Spring Cypress Rd, Spring, TX 77388",
      purpose: "Planned vehicle storage",
      vehicle_details: {
        make: "Honda",
        model: "Pilot",
        status: "Properly stored",
        location: "Commercial storage facility"
      }
    },
    activities_demonstrated: [
      "Vehicle operation", 
      "Navigation",
      "Physical mobility",
      "Planning capability",
      "Property management"
    ],
    related_events: [9, 11],
    verification_potential: "U-Haul security footage, facility access records, payment receipts, GPS data",
    attachments: []
  },
  {
    id: 11,
    date: "April 28, 2023",
    time: "17:30",
    title: "Primary Vehicle Acquisition and Return Travel",
    description: "Used rideshare service (Uber) to travel to Maaco auto body shop, collected previously serviced Honda Civic, and drove it back to residence in Conroe. Demonstrates normal vehicle operation following professional service.",
    type: "transportation_activity",
    category: "pre_incident",
    significance: "Documents acquisition of Honda Civic and normal driving behavior approximately 8.5 hours before incident. Establishes timeline of vehicle possession and proper functioning following professional service.",
    transportation_details: {
      first_leg: {
        mode: "Rideshare (Uber)",
        starting_location: "U-Haul facility, Spring, TX",
        destination: "Maaco auto body shop, Spring, TX"
      },
      second_leg: {
        mode: "Self-driven vehicle",
        vehicle: "Honda Civic",
        starting_location: "Maaco auto body shop, Spring, TX",
        destination: "550 Hermitage Ct., Conroe, TX",
        approximate_distance: "20-25 miles"
      }
    },
    activities_demonstrated: [
      "Rideshare usage", 
      "Vehicle inspection before acceptance",
      "Vehicle operation",
      "Navigation",
      "Highway driving capability"
    ],
    related_events: [9, 10],
    verification_potential: [
      "Uber trip records and payment receipt",
      "Maaco service completion records",
      "Maaco security footage",
      "Toll road records",
      "Traffic cameras"
    ],
    attachments: []
  },
{
  id: 12,
  date: "April 28, 2023",
  time: "19:45",
  title: "Evening Travel to Retail Destination",
  description: "Departed residence in Honda Civic en route to Micro Center electronics store in Houston for planned purchase/return transaction. Demonstrates intentional pre-planned evening travel.",
  type: "planned_travel",
  category: "pre_incident",
  significance: "Documents deliberate travel plans and vehicle operation approximately 6.25 hours before incident. Establishes purpose-driven destination requiring cognitive planning.",
  transportation_details: {
    mode: "Self-driven vehicle",
    vehicle: "Honda Civic",
    starting_location: "550 Hermitage Ct., Conroe, TX",
    destination: "Micro Center, 5305 S Rice Ave, Houston, TX 77081",
    approximate_distance: "45-50 miles",
    direction: "South",
    route: "I-45 South to Beltway 8"
  },
  activities_demonstrated: [
    "Travel planning", 
    "Vehicle operation",
    "Navigation",
    "Long-distance driving capability",
    "Evening driving"
  ],
  related_events: [14, 15],
  verification_potential: [
    "Toll road records",
    "Traffic cameras",
    "Cell phone location data",
    "Home security cameras (departure)",
    "Retail visit records (see events 14-15)"
  ],
  attachments: []
},
  {
    id: 13,
    date: "April 28, 2023",
    time: "20:00",
    title: "Gas Station Purchase Stop",
    description: "Made brief stop at gas station during trip to Micro Center. Purchased fuel and a six-pack of Mike's Hard Lemonade, which was placed unopened in the rear seat of the vehicle. This alcoholic beverage remained sealed and was not consumed.",
    type: "routine_stop",
    category: "pre_incident",
    significance: "Documents normal travel behavior approximately 6 hours before incident. Purchase of sealed alcoholic beverage that remained unopened contradicts allegations of continuous intoxication.",
    location_details: {
      type: "Gas station",
      approximate_location: "Along I-45 corridor between Conroe and Houston",
      activities: ["Vehicle refueling", "Retail purchase"]
    },
    purchase_details: {
      items: [
        "Gasoline",
        "Six-pack of Mike's Hard Lemonade (unopened)"
      ],
      payment_method: "Debit card (same as used at Micro Center later)",
      amount: "Unknown"
    },
    activities_demonstrated: [
      "Normal transaction processing",
      "Decision-making regarding fuel needs", 
      "Planning ahead (purchasing item for later use)",
      "Responsible behavior (not consuming alcohol while driving)"
    ],
    related_events: [12, 14, 15],
    verification_potential: [
      "Gas station security footage",
      "Credit/debit card transaction records",
      "Receipt (if retained)",
      "Gas station employee witnesses",
      "Presence of unopened beverage container in vehicle post-incident"
    ],
    attachments: []
  },
  {
    id: 14,
    date: "April 28, 2023",
    time: "20:45",
    title: "Micro Center Store Arrival",
    description: "Arrived at Micro Center electronics store in Houston after approximately one-hour drive from Conroe. This visit is confirmed by electronic receipts showing multiple transactions conducted during this shopping trip.",
    type: "verifiable_location",
    category: "pre_incident",
    significance: "Confirms subject's precise location and cognitive capacity for complex retail transactions approximately 5 hours before incident. Demonstrates normal shopping behavior, financial decision-making, and coherent interaction in retail environment.",
    location_details: {
      name: "Micro Center",
      address: "5305 S Rice Ave, Houston, TX 77081",
      business_type: "Electronics retailer",
      approximate_duration: "2+ hours based on transaction timestamps"
    },
    activities_demonstrated: [
      "Navigation to specific retail destination",
      "Product selection and decision-making",
      "Financial transactions",
      "Computer component knowledge (PC build parts)",
      "Customer interaction",
      "Complex purchase planning"
    ],
    related_events: [12, 13, 15],
    verification_potential: [
      "Store security camera footage",
      "Electronic transaction records",
      "Witness testimony from store employees",
      "Itemized receipts (attached)",
      "Store loyalty program records"
    ],
    evidence_details: {
      transaction_confirmation: "Multiple electronic receipts showing purchases and returns",
      transaction_time: "Receipt timestamps indicate arrival around 20:45 with transactions continuing until approximately 21:19",
      purchase_content: "Computer components and flight simulator equipment",
      payment_method: "Debit card ending in 5212"
    },
    attachments: [
      { name: "MicroCenter Receipt.pdf", path: "/evidence/exhibits/20230428_EXHIBIT-D19_MicroCenter-Receipt-FlightSim.pdf", type: "pdf", exhibitId: "D19" }
    ]
  },
  {
    id: 15,
    date: "April 28, 2023",
    time: "21:19",
    title: "Micro Center Return Transaction Completion",
    description: "Finalized multiple return transactions at Micro Center electronics store, documented with precise timestamp in email receipt. Returned high-value computer components and gaming peripherals totaling over $2,700, demonstrating complex financial decision-making and normal retail interaction.",
    type: "verifiable_transaction",
    category: "pre_incident",
    significance: "Provides concrete evidence of subject's location, cognitive capacity, and ability to conduct complex financial transactions approximately 4.5 hours before incident. Return process required lucid communication, product knowledge, and adherence to store policies.",
    location_details: {
      name: "Micro Center",
      address: "5305 S Rice Ave, Houston, TX 77081",
      transaction_area: "Customer service/returns counter",
      customer_service_rep: "Oscar E (as noted on receipt)"
    },
    transaction_details: {
      transaction_type: "Multiple merchandise returns",
      receipt_number: "155-RE-12667772",
      transaction_date: "04/28/2023",
      customer_name: "LANDON MAYO",
      total_refund_amount: "$952.57",
      payment_method: "Refund to debit card (ending 5212)",
      returned_items: [
        "TurtleBeach Velocity One Flight Xbox ($379.99)",
        "TurtleBeach Velocity One Rudder ($299.99)",
        "GuitaMMer ButtKicker Gamer2 ($199.99)"
      ],
      original_sale_reference: "155-PO-12633077"
    },
    activities_demonstrated: [
      "Complex return process navigation",
      "Memory recall of previous purchases",
      "Financial transaction completion",
      "Communication with store personnel",
      "Decision-making regarding product returns",
      "Adherence to store policies"
    ],
    related_events: [12, 13, 14],
    verification_potential: [
      "Store security camera footage",
      "Electronic transaction records",
      "Witness testimony from store employee 'Oscar E'",
      "Store loyalty program records",
      "Email receipt (attached)"
    ],
    attachments: [
      { name: "MicroCenter Return Receipt.pdf", path: "/evidence/exhibits/20230428_EXHIBIT-D19_MicroCenter-Receipt-FlightSim.pdf", type: "pdf", exhibitId: "D19" }
    ]
  },
  {
    id: 16,
    date: "April 28, 2023",
    time: "21:30",
    title: "Departure from Micro Center to Friend's Residence",
    description: "Departed Micro Center electronics store following completion of return transactions. Began approximately 25-mile drive north to friend's residence in Spring, TX area, consistent with normal evening travel pattern.",
    type: "travel_movement",
    category: "pre_incident",
    significance: "Documents continued normal movement and vehicle operation approximately 4 hours before incident. Establishes coherent decision-making regarding travel destinations and ability to operate vehicle after complex retail transactions.",
    transportation_details: {
      mode: "Self-driven vehicle",
      vehicle: "Honda Civic",
      starting_location: "Micro Center, 5305 S Rice Ave, Houston, TX 77081",
      destination: "Froemling residence, Spring, TX",
      approximate_distance: "25-30 miles",
      direction: "North",
      route: "Likely Beltway 8 to I-45 North"
    },
    activities_demonstrated: [
      "Vehicle operation after retail transactions",
      "Navigation to residential address",
      "Evening driving in metropolitan area",
      "Execution of planned travel route",
      "Social planning (visiting friend)"
    ],
    related_events: [14, 15, 17],
    verification_potential: [
      "Store parking lot/exit security cameras",
      "Traffic cameras along route",
      "Cell phone location data",
      "Toll road records (if applicable)",
      "Witness confirmation from residence occupants"
    ],
    attachments: []
  },
  {
    id: 17,
    date: "April 28, 2023",
    time: "22:00",
    title: "Social and Business Meeting at Friend's Residence",
    description: "Arrived at Froemling residence in Spring, TX following Micro Center shopping trip. Engaged in business discussions with Johnny and Richard Froemling, during which the condition and maintenance needs of Honda Civic were examined and discussed.",
    type: "social_meeting",
    category: "pre_incident",
    significance: "Documents continued normal social interaction and vehicle awareness approximately 3.5 hours before incident. Demonstrates cognitive capacity for business discussions and vehicle mechanical knowledge shortly before alleged intoxicated driving.",
    location_details: {
      name: "Froemling residence",
      address: "19422 Enchanted Oaks Dr, Spring, TX",
      location_type: "Private residence",
      approximate_duration: "2 hours"
    },
    social_interaction: {
      attendees: ["Johnny Froemling", "Richard Froemling", "Landon Mayo"],
      relationship: "Friends/business associates",
      discussed_topics: ["Business matters", "Vehicle condition assessment"],
      activities: ["Conversation", "Vehicle examination"]
    },
    vehicle_details: {
      vehicle: "2006 Honda Civic",
      examination_purpose: "Newly acquired vehicle",
      condition_noted: "Unknown, but demonstrated awareness of vehicle status"
    },
    activities_demonstrated: [
      "Social interaction with multiple individuals",
      "Business-related communication",
      "Technical vehicle knowledge application",
      "Memory and recall during discussions",
      "Problem-solving regarding vehicle issues"
    ],
    related_events: [16, 18],
    verification_potential: [
      "Witness testimony from Johnny and Richard Froemling",
      "Cell phone location data",
      "Text messages or calls confirming visit",
      "Security cameras at residence (if available)",
      "Documentation of business matters discussed"
    ],
    attachments: []
  },
  {
    id: 18,
    date: "April 28, 2023",
    time: "23:45",
    title: "Technical Equipment Assembly and Configuration",
    description: "Conducted complex technical assembly and configuration of 3D printer and computer equipment at Froemling residence. This activity demonstrates high-level cognitive functioning and fine motor skills shortly before incident.",
    type: "technical_activity",
    category: "pre_incident",
    significance: "Documents sophisticated technical capabilities requiring concentration, dexterity, and problem-solving approximately 2 hours before incident. Ability to successfully set up complex technology contradicts alleged cognitive impairment.",
    location_details: {
      name: "Froemling residence",
      address: "19422 Enchanted Oaks Dr, Spring, TX",
      specific_area: "Likely home office or workshop area",
      equipment_placement: "Indoor installation requiring power sources and workspace"
    },
    equipment_details: {
      items: [
        "3D printer (model unknown)",
        "Computer system (likely related to earlier Micro Center purchases/returns)"
      ],
      setup_requirements: [
        "Physical assembly",
        "Software installation/configuration",
        "Calibration",
        "Network connectivity setup"
      ],
      complexity_level: "High - requires technical knowledge and precision"
    },
    activities_demonstrated: [
      "Technical documentation comprehension",
      "Fine motor control for assembly",
      "Software configuration understanding",
      "Troubleshooting capabilities",
      "Spatial reasoning for equipment placement",
      "Digital interface navigation"
    ],
    related_events: [15, 17, 19],
    verification_potential: [
      "Witness testimony from Froemling residents",
      "Photographs of completed setup (if taken)",
      "Digital timestamps of software installation/configuration",
      "Hardware serial numbers of installed equipment",
      "Network connection logs for devices"
    ],
    attachments: []
  },
  {
    id: 19,
    date: "April 29, 2023",
    time: "01:30",
    title: "Organized Departure and Firearm Securing Procedures",
    description: "Executed systematic preparation for departure from Froemling residence, including careful securing of firearms in appropriate vehicle compartments. Multiple firearms were properly stored in trunk, with ammunition component (magazine) temporarily placed in pocket during loading process.",
    type: "departure_preparation",
    category: "pre_incident",
    significance: "Demonstrates organized safety protocols and methodical preparation approximately 30 minutes before incident. Firearm handling requires clear mental faculties, spatial awareness, and adherence to safety procedures, contradicting alleged impairment.",
    location_details: {
      name: "Froemling residence driveway/parking area",
      address: "19422 Enchanted Oaks Dr, Spring, TX",
      lighting_conditions: "Likely nighttime/limited visibility",
      privacy_level: "Private property"
    },
    firearm_handling: {
      items_secured: [
        "Multiple rifles (properly stored in trunk)",
        "Sidearm handgun (properly stored in trunk)",
        "9mm magazine (temporarily on person during loading process)"
      ],
      safety_protocols: "Demonstrated proper firearm transportation procedures by securing weapons in vehicle trunk, separate from passenger compartment",
      legal_compliance: "Followed Texas laws regarding firearm transportation in vehicles"
    },
    activities_demonstrated: [
      "Firearm safety knowledge and application",
      "Sequential task completion",
      "Safety-conscious decision making",
      "Physical coordination handling multiple items",
      "Memory of firearm locations and components",
      "Adherence to legal transportation requirements"
    ],
    related_events: [18, 20, 27],
    verification_potential: [
      "Witness testimony from Froemling residents",
      "Firearm serial numbers and registration documentation",
      "Security cameras at residence (if available)",
      "Physical evidence of magazine detected during later events (see event 27)",
      "Locations of items within vehicle post-incident"
    ],
    attachments: []
  },
  {
    id: 20,
    date: "April 29, 2023",
    time: "02:00",
    title: "Late-Night Fast Food Purchase",
    description: "Stopped at Jack in the Box restaurant while en route to destination. Ordered and received a complete meal consisting of a grilled chicken club sandwich, curly fries, and a medium Coca-Cola soft drink.",
    type: "food_purchase",
    category: "immediately_pre_incident",
    significance: "Demonstrates normal appetite, decision-making, and transaction capabilities just prior to incident. Meal selection shows coherent food preferences and ability to place and pay for a specific order, contradicting severe impairment.",
    location_details: {
      business_type: "Fast food restaurant",
      name: "Jack in the Box",
      approximate_location: "9300 TX-242, Conroe, TX 77385",
      service_method: "Drive-through",
      duration: "Approximately 10-15 minutes for ordering, payment, and receipt"
    },
    food_order_details: {
      items: [
        "Grilled chicken club sandwich",
        "Curly fries",
        "Medium Coca-Cola"
      ],
      order_complexity: "Multiple item selection with specific preferences",
      payment_method: "Debit card"
    },
    activities_demonstrated: [
      "Menu navigation and selection",
      "Verbal communication with restaurant staff",
      "Financial transaction completion",
      "Multi-tasking (ordering while in vehicle)",
      "Normal appetite and food consumption",
      "Beverage choice indicating no alcohol consumption"
    ],
    related_events: [19, 21],
    verification_potential: [
      "Restaurant security camera footage",
      "Drive-through order records",
      "Credit/debit card transaction records",
      "Food packaging/receipt found in vehicle post-incident",
      "Restaurant employee witness testimony"
    ],
    attachments: []
  },
  {
    id: 21,
    date: "April 29, 2023",
    time: "02:10",
    title: "Emergency Assistance Call Received",
    description: "Received urgent phone call from fiancée reporting vehicle trouble (flat tire). Immediately altered travel plans to provide roadside assistance at her location, demonstrating clear decision-making and prioritization under time pressure.",
    type: "emergency_response",
    category: "incident_trigger",
    significance: "Establishes direct precipitating cause for subject's presence at incident location. This unexpected emergency call documents a legitimate reason for late-night driving and explains sudden change in route/destination, contradicting any suggestion of aimless or impaired wandering.",
    communication_details: {
      caller: "Fiancée (name not specified in records)",
      call_type: "Emergency assistance request",
      communication_method: "Phone call",
      nature_of_emergency: "Vehicle disabled due to flat tire",
      response_decision: "Immediate travel to provide assistance"
    },
    assistance_plan: {
      destination_change: "Redirected from previous route to fiancée's location",
      intended_assistance: "Roadside tire change or vehicle repair",
      urgency_level: "High - nighttime roadside emergency",
      route_planning: "Rapid navigation to new destination"
    },
    activities_demonstrated: [
      "Phone communication during driving",
      "Emergency situation assessment",
      "Rapid decision-making",
      "Route recalculation",
      "Caretaking responsibility",
      "Technical assistance preparation"
    ],
    related_events: [20, 22],
    verification_potential: [
      "Phone records showing incoming call",
      "Fiancée witness testimony",
      "Cell tower location data showing route change",
      "Text messages or other communications about the situation",
      "Fiancée's vehicle service records (if applicable)"
    ],
    attachments: []
  },
  {
    id: 22,
    date: "April 29, 2023",
    time: "Early morning hours",
    title: "Single-Vehicle Accident",
    description: "Vehicle experienced tire blowout causing collision with roadside barrier near Shenandoah.",
    type: "incident",
    attachments: [],
    contributingFactors: "Tire blowout (documented in vehicle history report and police communications)",
    response: "Waited 10-15 minutes at scene before officer arrival",
    medical: "Voluntarily transported to Memorial Hermann Hospital for evaluation",
    policeAction: "No field sobriety test administered; arrested only upon hospital discharge"
  },
  {
    id: 23,
    date: "April 29, 2023",
    time: "02:18",
    title: "Vehicle Safety Recall Documentation",
    description: "The 2006 Honda Civic involved in the incident had multiple documented safety recalls, including critical issues affecting airbag deployment systems. These manufacturer-identified defects could impact vehicle behavior during collisions.",
    type: "vehicle_documentation",
    category: "contextual_evidence",
    significance: "Establishes pre-existing mechanical vulnerabilities in the vehicle that could affect both handling and emergency safety systems during incident. Manufacturer recalls document known defects that may have contributed to incident circumstances independent of driver condition.",
    vehicle_details: {
      make_model: "2006 Honda Civic",
      vin: "2HGFG12656H580199",
      registration_status: "Currently registered in Texas as of 04/07/2023",
      ownership_history: "Multiple previous owners across multiple states"
    },
    recall_information: {
      total_recalls: "15 of 15 Records",
      critical_safety_recalls: [
        "NHTSA ID 18V268000: Passenger airbag may deploy improperly in crash, increasing injury risk",
        "NHTSA ID 17V030000: Airbag inflator rupture risk causing metal fragments",
        "NHTSA ID 19V378000: Incorrectly installed airbag inflator may not deploy properly",
        "NHTSA ID 17V545000: Incorrectly installed passenger airbag may deploy improperly",
        "NHTSA ID 19V502000: Passenger airbag inflator explosion risk"
      ],
      mechanical_recalls: [
        "NHTSA ID 05V509000: Accelerator pedal issue causing stuck throttle risk",
        "NHTSA ID 07V399000: Wheel speed sensor issues causing wheel loosening risk"
      ],
      recall_status: "Unknown if repairs were completed"
    },
    relevance_to_case: {
      potential_mechanical_factors: [
        "Airbag deployment problems could affect post-collision injuries",
        "Accelerator pedal issues could contribute to unexpected acceleration",
        "Wheel sensor problems could affect vehicle stability"
      ],
      investigative_gaps: "No documentation of whether recall repairs were performed"
    },
    related_events: [22, 24],
    verification_potential: [
      "Honda dealer service records",
      "Previous owner maintenance documentation",
      "NHTSA recall repair database",
      "Post-incident vehicle mechanical inspection",
      "Airbag control module data retrieval"
    ],
    attachments: [
      { name: "Vehicle History with Recall Report.pdf", path: "/evidence/exhibits/20230427_EXHIBIT-D6_Vehicle_History_with_Recall_Report.pdf", type: "pdf", exhibitId: "D6" }
    ]
  },
  {
    id: 24,
    date: "April 29, 2023",
    time: "02:30",
    title: "First Responders Arrival Sequence",
    description: "Following the vehicle incident, Shenandoah Police Department officers were first to arrive on scene, with Fire Department personnel arriving shortly thereafter. This established the initial emergency response timeline.",
    type: "emergency_services_response",
    category: "incident_documentation",
    significance: "Documents official first responder presence and timeline. The Shenandoah Police officers were first on scene and would have made initial observations of subject's condition and scene circumstances before any medical intervention.",
    response_details: {
      first_responder: "Shenandoah Police Department",
      secondary_responder: "Fire Department (likely Shenandoah FD)",
      response_time: "Unknown time between incident and first police arrival",
      sequence: "Police arrived before Fire Department personnel"
    },
    law_enforcement_actions: {
      agency: "Shenandoah Police Department",
      initial_assessment: "Vehicle incident requiring emergency response",
      documentation_status: "Original police records and video lost due to city-wide system hack"
    },
    evidence_limitations: {
      missing_documentation: "According to Sergeant Brockoff (Shenandoah PD), city systems were hacked resulting in loss of video evidence and possibly reports",
      confirmation_source: "Recorded conversation with Sergeant Brockoff on May 3, 2024",
      impact: "Critical first responder observations and documentation unavailable"
    },
    related_events: [23, 25, 26],
    verification_potential: [
      "Police dispatch records",
      "Officer testimony (first responders)",
      "Fire Department incident report",
      "Emergency medical service logs",
      "911 call recordings"
    ],
    attachments: []
  },
  {
    id: 25,
    date: "April 29, 2023",
    time: "02:45 AM",
    title: "Police Confirmation of Missing Evidence",
    description: "Call with Sergeant Brockoff confirms city was hacked, resulting in loss of dashcam footage. No police report was filed by the first responding officer.",
    type: "police",
    attachments: [
      { name: "Shenandoah Police Call Transcript.pdf", path: "/evidence/documents/20240503_EXHIBIT-C1B_Shenandoah-Police-Call-Transcript.pdf", type: "pdf", exhibitId: "C1B", audioPath: "/evidence/audio/20240503_EXHIBIT-C1A_Shenandoah-Police-Audio.mp3" }
    ]
  },
  {
    id: 26,
    date: "April 29, 2023",
    time: "03:20",
    title: "Emergency Medical Transport Initiation",
    description: "Self-ambulatory to emergency vehicle, then received standard trauma protocol including spinal precautions and intravenous access. Entered ambulance voluntarily and cooperated with EMS personnel throughout medical interventions.",
    type: "medical_transport",
    category: "post_incident_medical",
    significance: "Documents physical mobility, consciousness, and cooperation with medical personnel. Self-ambulation to ambulance demonstrates motor function and voluntary movement, contradicting severe impairment. Standard trauma protocols were followed despite subject's ambulatory status.",
    ambulance_details: {
      provider: "Likely Montgomery County EMS or private ambulance service",
      destination: "Memorial Hermann The Woodlands Hospital (confirmed by medical records)",
      departure_condition: "Ambulatory initially, then secured on stretcher per protocol"
    },
    medical_interventions: {
      mobility_status: "Self-ambulatory to ambulance before placement on stretcher",
      spinal_precautions: "Cervical collar applied as precautionary measure",
      vascular_access: "Intravenous line established for potential medication administration",
      monitoring: "Likely vital signs monitoring initiated (BP, pulse, oxygen saturation)",
      consciousness_level: "Alert enough to follow directions and cooperate with procedures"
    },
    evidence_of_capacity: {
      physical_abilities: "Independent walking to ambulance",
      cooperation: "Voluntary compliance with medical protocols",
      communication: "Presumed verbal exchanges with EMS personnel",
      "decision-making": "Consent to medical transport and treatment"
    },
    related_events: [24, 25, 27],
    verification_potential: [
      "EMS patient care report/run sheet",
      "Ambulance crew testimony",
      "Hospital admission records",
      "Paramedic documentation of patient condition",
      "EMS dispatch records"
    ],
    attachments: []
  },
  {
    id: 27,
    date: "April 29, 2023",
    time: "03:55 AM",
    title: "Hospital Arrival",
    description: "Arrived at Memorial Hermann Hospital, where triage was initiated.",
    type: "medical",
    attachments: [
      { name: "Hospital Ambulatory Summary.pdf", path: "/evidence/exhibits/20230429_EXHIBIT-D2_Hospital-Ambulatory-Summary.pdf", type: "pdf", exhibitId: "D2" }
    ]
  },
  {
    id: 28,
    date: "April 29, 2023",
    time: "04:30 AM",
    title: "Emergency Medical Personnel Departure",
    description: "EMS staff left hospital, no police were present during this time.",
    type: "medical",
    attachments: []
  },
  {
    id: 29,
    date: "April 29, 2023",
    time: "05:00 AM",
    title: "Hospital Confirms Gun Clip Left at ER",
    description: "Call with Memorial Hermann The Woodlands Patient Relations confirming gun clip was left behind during ER visit",
    type: "medical",
    attachments: [
      { name: "Memorial Hermann Hospital Gun Clip Call.pdf", path: "/evidence/documents/20241220_EXHIBIT-C3_Memorial_Hermann_Hospital_Gun_Clip_Left_at_ER.pdf", type: "pdf", exhibitId: "C3", audioPath: "/evidence/audio/20241220_EXHIBIT-C3_Memorial_Hermann_Hospital_Gun_Clip_Left_at_ER.mp3" }
    ]
  },
  {
    id: 30,
    date: "April 29, 2023",
    time: "05:30 AM",
    title: "Blood Draw",
    description: "Nurse drew blood for medical tests using alcohol prep pads and red-capped vials.",
    type: "medical",
    attachments: []
  },
  {
    id: 31,
    date: "April 29, 2023",
    time: "08:00 AM",
    title: "Doctor Consultation",
    description: "Doctor reported fractured left mandible coronoid process, abrasions to arms from airbag, and head injury. Prescribed pain medication and recommended PCP follow-up.",
    type: "medical",
    attachments: [
      { name: "VA Medical History.pdf", path: "/evidence/exhibits/20240523_EXHIBIT-D4_VA-Medical-History.pdf", type: "pdf", exhibitId: "D4" }
    ]
  },
  {
    id: 32,
    date: "April 29, 2023",
    time: "Morning hours",
    title: "Police Inquiry Without Warrant",
    description: "Montgomery County officers arrived at Memorial Hermann Hospital seeking blood test results without proper documentation. Medical staff indicated legal requirements for medical record access were not met, yet test results were later obtained without patient consent.",
    type: "legal_violation",
    related_evidence: ["Hospital Ambulatory Summary", "Discrepant Lab Results", "Hair Follicle Test"]
  },
  {
    id: 33,
    date: "April 29, 2023",
    time: "Morning hours after hospital discharge",
    title: "Arrest Without Proper Procedure",
    description: "Montgomery County officers arrested me for DWI upon hospital discharge without administering field sobriety tests despite my earlier requests. No Miranda rights were read. Arrest occurred hours after alleged incident with no observation of driving.",
    type: "legal_violation",
    attachments: [
      { name: "DWI Felony Charge.pdf", path: "/evidence/exhibits/20230501_EXHIBIT-C6_Montgomery-DWI-Felony-Charge.pdf", type: "pdf", exhibitId: "C6" }
    ],
    related_evidence: ["Transcript of Shenandoah Police Call", "Hospital Discharge Records"]
  },
  {
    id: 34,
    date: "April 29, 2023",
    time: "En route to jail after hospital discharge",
    title: "Officer's Doubt About Evidence",
    description: "During transport, arresting officer made phone call expressing significant doubt about evidence, stating: 'It should be an okay case.... the blood though... eh' - indicating concerns about reliability of blood test results that contradicted physical observations.",
    type: "exculpatory_evidence",
    related_documents: ["Hair Follicle Test Results", "Hospital Ambulatory Summary with Discrepant Lab Values"]
  },
  {
    id: 35,
    date: "June 30, 2023",
    time: "4:36 PM",
    title: "Independent Drug Testing Evidence",
    description: "Underwent comprehensive 6-month hair follicle drug testing at S.A.F.E. Drug Testing in Conroe, certified by OMEGA Laboratories. Results conclusively negative for all substances, directly contradicting hospital blood test claims from April 29 incident.",
    type: "exculpatory_evidence",
    attachments: [
      { name: "Hair Follicle Test Results.pdf", path: "/evidence/exhibits/20230630_EXHIBIT-D1_Hair-Follicle-Test-Results.pdf", type: "pdf", exhibitId: "D1" }
    ],
    significance: "Scientific evidence refuting blood alcohol and drug claims that formed basis for charges"
  },
  {
    id: 36,
    date: "July 25, 2023",
    time: "Afternoon",
    title: "Bond Supervision Issues",
    description: "Bond Officer Stephen Charles claimed missed appointments despite documented communication issues. Email evidence shows I had sent multiple unanswered messages since June 29, with officer acknowledging receipt only after threatening violation. VA appointments and transportation difficulties were disregarded.",
    type: "procedural_issue",
    attachments: [
      { name: "Probation Conflict Email.pdf", path: "/evidence/exhibits/20230725_EXHIBIT-C2_Email-Probation-Conflict-Stephen-Charles.pdf", type: "pdf", exhibitId: "C2" },
      { name: "Auto Reply Email.pdf", path: "/evidence/exhibits/20230725_EXHIBIT-E1A_Auto_Reply_Drug testing_ Landon Mayo.pdf", type: "pdf", exhibitId: "E1A" }
    ],
    related_issues: ["Communication failures", "Veteran healthcare access", "Disability accommodation"]
  },
  {
    id: 37,
    date: "July 25, 2023",
    time: "2:57 PM",
    title: "Documentation of Communication Failures",
    description: "Email chain documenting month-long pattern of unanswered communications to Bond Officer Stephen Charles. Evidence shows multiple attempts to explain VA medical appointments and transportation issues beginning June 29, with officer only responding with bond violation threats on July 25 despite being out of office during critical period.",
    type: "evidence_of_procedural_failure",
    attachments: [
      { name: "Probation Conflict Email.pdf", path: "/evidence/exhibits/20230725_EXHIBIT-C2_Email-Probation-Conflict-Stephen-Charles.pdf", type: "pdf", exhibitId: "C2" },
      { name: "Auto Reply Email.pdf", path: "/evidence/exhibits/20230725_EXHIBIT-E1A_Auto_Reply_Drug testing_ Landon Mayo.pdf", type: "pdf", exhibitId: "E1A" }
    ],
    significance: "Demonstrates systematic communication breakdown that directly led to first bond violation and incarceration period"
  },
  {
    id: 38,
    date: "February 12, 2024",
    time: "9:00 AM",
    title: "VA Benefits Terminated",
    description: "VA benefits were terminated due to a false classification as a fugitive felon. This classification was based on erroneous arrest claims, despite evidence proving compliance with legal obligations. The termination caused significant financial and healthcare hardships.",
    type: "consequence",
    attachments: [
      { name: "VA Benefits Termination.pdf", path: "/evidence/exhibits/20240212_EXHIBIT-D12_Termination-of-VA-Benefits.pdf", type: "pdf", exhibitId: "D12" }
    ],
    significance: "Highlights the cascading impact of false legal actions on veteran support systems."
  },
  {
    id: 39,
    date: "May 6, 2024",
    time: "9:11 AM",
    title: "Confirmation of Missing Evidence",
    description: "Email from court-appointed attorney Steve Dennis confirming multiple unsuccessful attempts to obtain critical evidence. Message confirms both Shenandoah Police and Montgomery County Sheriff's Office had failed to provide requested dashcam footage and reports despite formal requests made over a month prior.",
    type: "evidence_suppression",
    attachments: [
      { name: "Dennis Email Re Evidence Request.pdf", path: "/evidence/exhibits/20240506_EXHIBIT-C1_Dennis-Email-Re-Evidence-Request.pdf", type: "pdf", exhibitId: "C1" },
      { name: "Shenandoah Police Call Transcript.pdf", path: "/evidence/exhibits/20240503_EXHIBIT-C1B_Shenandoah-Police-Call-Transcript.pdf", type: "pdf", exhibitId: "C1B" }
    ],
    significance: "Formal documentation that defense counsel was unable to obtain critical exculpatory evidence despite proper legal requests"
  }
];

const TimelinePage = () => {
  const [events, setEvents] = useState(HARDCODED_TIMELINE);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'alibi', 'incident', 'police', etc.
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExhibit, setSelectedExhibit] = useState(null);

  // Changed from React.useEffect to useEffect since we're now importing it
  useEffect(() => {
    let isMounted = true; // Flag to track if component is mounted

    // Set a timeout to simulate loading for a brief moment
    setTimeout(() => {
      if (isMounted) {
        // Process the hardcoded timeline data
        const processedData = HARDCODED_TIMELINE.map(event => {
          // Create a standardized event object with defaults
          const standardizedEvent = {
            ...event,
            originalDate: event.date,
            // Ensure every event has a properly formatted date if possible
            date: event.date ? parseAndFormatDate(event.date) : event.date
          };
          
          // Fix VA Benefits Termination event to show correct 2025 date
          if (event.title?.includes('VA Benefits Terminated') || 
              (event.description?.includes('benefits terminated') && event.description?.includes('false classification')) ||
              (event.title?.includes('Benefits') && event.description?.includes('fugitive felon'))) {
            return {
              ...standardizedEvent,
              date: '2025-02-11',
              time: '09:00' // Always force 9:00 for this event
            };
          }

          // Fix Public Defender Evidence Request event date (force to 2024-05-05)
          if (event.title?.includes('Public Defender Evidence Request') ||
              (event.description?.includes('Steve Dennis') && event.description?.includes('evidence')) ||
              (event.type === 'legal' && (event.date?.includes('2023-05-05') || event.date?.includes('2024-05-05')))) {
            return {
              ...event,
              date: '2024-05-05',
              time: '01:30', // Always force 1:30 for this event
              originalDate: event.date
            };
          }

          // Fix Gun Clip event first to ensure proper sorting later
          if (event.title?.includes('Hospital Confirms Gun Clip Left') ||  
              event.title?.includes('Gun Clip Left at ER') ||
              (event.description?.includes('gun clip') && event.description?.includes('left behind'))) {
            return {
              ...event,
              date: '2024-12-20',
              time: '05:00', // Always force 5:00 for this event
              originalDate: event.date
            };
          }

          // Fix Police Confirmation event dates (force to 2024-05-03)
          if ((event.title?.includes('Police Confirmation') || event.title?.includes('Missing Evidence')) &&
              (event.date === '2023-04-29' || event.date === '2023-05-03' || event.date === '2024-05-03')) {
            return {
              ...event,
              date: '2024-05-03',
              time: '02:45', // Always force 2:45 for this event
              originalDate: event.date
            };
          }

          // Ensure all dates are in proper format
          if (event.date && typeof event.date === 'string') {
            if (!event.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
              try {
                const dateParts = new Date(event.date);
                if (!isNaN(dateParts.getTime())) {
                  const formattedDate = `${dateParts.getFullYear()}-${String(dateParts.getMonth() + 1).padStart(2, '0')}-${String(dateParts.getDate()).padStart(2, '0')}`;
                  return {
                    ...event,
                    date: formattedDate,
                    originalDate: event.date
                  };
                }
              } catch (e) {
                console.error(`Error parsing date: ${event.date}`, e);
              }
            }
          }
          return event;
        });

        // Sort by date and time with special handling for specific events
        const sortedData = processedData.sort((a, b) => {
          // Use strict ISO date-time for full chronological order
          const isoA = `${a.date}T${(a.time || '00:00').padStart(5, '0')}:00`;
          const isoB = `${b.date}T${(b.time || '00:00').padStart(5, '0')}:00`;
          if (isoA < isoB) return -1;
          if (isoA > isoB) return 1;
          return 0;
        });

        // Fix any additional event details and attachments
        const updatedEvents = sortedData.map(event => {
          // Gun Clip event handling
          if (isGunClipEvent(event)) {
            return {
              ...event,
              date: '2024-12-20',
              time: event.time || '05:00',
              attachments: event.attachments?.map(attachment => {
                if (attachment.name?.includes('Gun Clip') || attachment.exhibitId === 'C3') {
                  return {
                    ...attachment,
                    path: '/evidence/documents/20241220_EXHIBIT-C3_Herman_Memorial_Hospital_Gun_Clip_Left_at_ER.pdf'
                  };
                }
                return attachment;
              }) || []
            };
          }

          // Police Confirmation event handling
          if (isPoliceConfirmationEvent(event)) {
            return {
              ...event,
              date: '2024-05-03',
              time: event.time || '02:45',
              type: 'police'
            };
          }

          // VA Benefits event handling
          if (isVABenefitsEvent(event)) {
            return {
              ...event,
              date: '2025-02-11',
              time: event.time || '09:00'
            };
          }

          // Public Defender event handling
          if (isPublicDefenderEvent(event)) {
            return {
              ...event,
              date: '2024-05-05',
              time: event.time || '01:30'
            };
          }

          return event;
        });

        setEvents(updatedEvents);
        setLoading(false);
      }
    }, 500); // Short timeout to simulate loading

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []);

  // Add a useEffect for modal event listener cleanup
  useEffect(() => {
    // Handler for keyboard escape key to close modal
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && selectedEvent) {
        setSelectedEvent(null);
      }
    };

    // Only attach event listeners if modal is open
    if (selectedEvent) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    // Clean up event listeners when component unmounts or when selectedEvent changes
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [selectedEvent]);

  // Event handlers
  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleOpenPdf = (pdfPath) => {
    openPdfInNewWindow(pdfPath);
  };

  // Get unique event types for filter dropdown
  const eventTypes = ['all', ...new Set(events.map(event => event.type))];

  // Helper function to highlight recent events
  const isRecentEvent = (dateString) => {
    // Check if the event is from 2024 or later
    return dateString && (dateString.includes('2024') || dateString.includes('2025'));
  };

  // Helper function to identify the specific Police Confirmation event
  const isPoliceConfirmationEvent = (event) => {
    return (event.title?.includes('Police Confirmation of Missing Evidence')) || 
           (event.title?.includes('Police') && event.title?.includes('Confirmation') && 
            (event.date === '2024-05-03' || event.date === '2023-05-03' || event.date === '2023-04-29'));
  };

  // Helper function to identify the Herman Memorial Hospital Gun Clip event
  const isGunClipEvent = (event) => {
    return event.title?.includes('Gun Clip Left') || 
           (event.title?.includes('Hospital') && event.title?.includes('Gun Clip')) || 
           event.title?.toLowerCase().includes('gun clip') ||
           event.description?.toLowerCase().includes('gun clip');
  };

  // Helper function to identify the VA Benefits Termination event
  const isVABenefitsEvent = (event) => {
    return event.title?.includes('VA Benefits Terminated') || 
           event.title?.includes('VA Benefits') || 
           (event.description?.includes('benefits terminated') && event.description?.includes('false classification')) ||
           (event.type === 'consequence' && event.date === '2025-02-11');
  };

  // Helper function to identify the Public Defender Evidence Request event
  const isPublicDefenderEvent = (event) => {
    return event.title?.includes('Public Defender Evidence Request') || 
           (event.description?.includes('Steve Dennis') && event.description?.includes('evidence')) ||
           (event.type === 'legal' && event.date?.includes('2023-05-05'));
  };

  // This function is to update incorrect dates in the UI display
  const getDisplayDate = (event) => {
    // Special case for the VA Benefits event - always show February 11, 2025
    if (isVABenefitsEvent(event)) {
      return 'February 11, 2025';
    }
    // Special case for the Police Confirmation event - always show May 3, 2024
    if (isPoliceConfirmationEvent(event)) {
      return 'May 3, 2024';
    }
    // Special case for the Public Defender event - always show May 5, 2024
    if (isPublicDefenderEvent(event)) {
      return 'May 5, 2024';
    }
    // Special case for the Herman Memorial Hospital Gun Clip event - always show December 20, 2024
    if (isGunClipEvent(event)) {
      return 'December 20, 2024';
    }
    // For all other events, use the regular date formatting
    return formatDate(event.date);
  };

  // Sort events before filtering to ensure they appear in the correct order
  const orderedEvents = [...events].sort((a, b) => {
    // Place VA Benefits event in 2025
    if (isVABenefitsEvent(a) && !isVABenefitsEvent(b)) {
      return 1; // VA Benefits event comes later (in 2025)
    }
    if (isVABenefitsEvent(b) && !isVABenefitsEvent(a)) {
      return -1; // Other events come before VA Benefits event
    }

    // Place Gun Clip event after December 19, 2024 events
    if (isGunClipEvent(a) && !isGunClipEvent(b)) {
      if (a.date < '2024-12-20') return 1;
    }
    if (isGunClipEvent(b) && !isGunClipEvent(a)) {
      if (b.date < '2024-12-20') return -1;
    }

    // Always place Police Confirmation event from 2024 in its chronological position
    if (isPoliceConfirmationEvent(a) && !isPoliceConfirmationEvent(b)) {
      return 1; // Move the Police Confirmation event to a later position
    }
    if (isPoliceConfirmationEvent(b) && !isPoliceConfirmationEvent(a)) {
      return -1; // Move the Police Confirmation event to a later position
    }

    return 0; // Keep original order for other events
  });

  // Filter events based on type and search term
  const filteredEvents = orderedEvents.filter(event => {
    const matchesFilter = filter === 'all' || event.type === filter;
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Handle correct PDF path for specific events
  const getCorrectPdfPath = (event, attachment) => {
    // For Herman Memorial Hospital Gun Clip event
    if (isGunClipEvent(event) && 
        (attachment.name?.includes('Gun Clip') || attachment.exhibitId === 'C3')) {
      return '/evidence/documents/20241220_EXHIBIT-C3_Herman_Memorial_Hospital_Gun_Clip_Left_at_ER.pdf';
    }
    return attachment.path;
  };

  // UI rendering
  return (
    <div className="container mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Case Timeline</h1>
        <p className="text-gray-600">
          Timeline of events from April 28, 2023 to present, including critical 2024 developments such as the Police Confirmation of Missing Evidence on May 3, 2024. This chronology establishes the sequence of events leading up to and following the incident.
        </p>
      </div>

      {/* Search and Filter Controls */}
      <div className="mb-6 flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
        <div className="flex-grow">
          <input
            type="text"
            placeholder="Search timeline..."
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        <div className="w-full md:w-64">
          <select
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filter}
            onChange={handleFilterChange}
          >
            {eventTypes.map((type, index) => (
              <option key={index} value={type}>
                {type === 'all' ? 'All Events' : type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading timeline...</p>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-600">No events found matching your criteria.</p>
        </div>
      ) : (
        <div className="relative border-l-4 border-blue-200 ml-6 pl-8 pb-8">
          {filteredEvents.map((event, index) => (
            <div 
              key={event.id} 
              className="mb-10 relative cursor-pointer"
              onClick={() => handleEventClick(event)}
            >
              {/* Timeline dot - Add special styling for Police Confirmation event */}
              <div 
                className={`absolute w-6 h-6 rounded-full -left-11 border-4 border-white ${getEventTypeColor(event.type)} ${isPoliceConfirmationEvent(event) ? 'ring-2 ring-offset-2 ring-red-500 animate-pulse' : isRecentEvent(event.date) ? 'ring-2 ring-offset-2 ring-red-300' : ''}`}
              ></div>

              {/* Date/Time - Add highlighting for 2024 events and use corrected date for Police Confirmation */}
              <div className={`text-sm mb-1 flex items-center ${isPoliceConfirmationEvent(event) ? 'text-red-800 font-bold' : isRecentEvent(event.date) ? 'text-red-700 font-semibold' : 'text-gray-500'}`}>
                <span>{getDisplayDate(event)}</span>
                {event.time && (
                  <>
                    <span className="mx-1">•</span>
                    <span>{formatTime(event.time)}</span>
                  </>
                )}
                {isPoliceConfirmationEvent(event) && (
                  <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">Critical Evidence</span>
                )}
              </div>

              {/* Event Content */}
              <div className={`bg-white rounded-lg shadow-sm p-5 hover:shadow-md transition-shadow ${isPoliceConfirmationEvent(event) ? 'border-l-4 border-red-600 bg-red-50' : isRecentEvent(event.date) ? 'border-l-4 border-red-400' : ''}`}>
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-navy">{event.title}</h3>
                  <span 
                    className={`${getEventTypeBadgeColor(event.type)} text-xs font-medium px-2.5 py-0.5 rounded-full`}
                  >
                    {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-4">{event.description}</p>

                {/* Attachments */}
                {event.attachments && event.attachments.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Related Evidence:</p>
                    <div className="flex flex-wrap gap-2">
                      {event.attachments.map((attachment, idx) => (
                        <button
                          key={idx}
                          onClick={e => {
                            e.stopPropagation();
                            setSelectedExhibit({ event, attachment });
                          }}
                          className="text-blue-600 text-sm hover:underline focus:outline-none flex items-center bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {attachment.exhibitId ? `Exhibit ${attachment.exhibitId}` : attachment.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-navy">{selectedEvent.title}</h2>
                <button 
                  onClick={() => setSelectedEvent(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mb-4">
                <span 
                  className={`${getEventTypeBadgeColor(selectedEvent.type)} text-sm font-medium px-2.5 py-0.5 rounded-full mr-2`}
                >
                  {selectedEvent.type.charAt(0).toUpperCase() + selectedEvent.type.slice(1)}
                </span>
                <span className="text-gray-500">
                  {isPublicDefenderEvent(selectedEvent) ? 'May 5, 2024' :
                   isVABenefitsEvent(selectedEvent) ? 'February 11, 2025' : 
                   isPoliceConfirmationEvent(selectedEvent) ? 'May 3, 2024' : 
                   isGunClipEvent(selectedEvent) ? 'December 20, 2024' : 
                   formatDate(selectedEvent.date)}
                  {selectedEvent.time && ` • ${formatTime(selectedEvent.time)}`}
                </span>
              </div>
              <p className="text-gray-700 mb-6">{selectedEvent.description}</p>

              {/* Attachments Section */}
              {selectedEvent.attachments && selectedEvent.attachments.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Related Evidence</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedEvent.attachments.map((attachment, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleOpenPdf(getCorrectPdfPath(selectedEvent, attachment))}
                        className="flex items-center p-3 border rounded-lg hover:bg-gray-50 text-left"
                      >
                        <div className="bg-blue-100 p-2 rounded mr-3">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium">{attachment.exhibitId ? `Exhibit ${attachment.exhibitId}` : attachment.name}</p>
                          <p className="text-sm text-gray-500">{attachment.type === 'pdf' ? 'PDF Document' : 'Document'}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-2">Event Details</h3>
                <ul className="text-sm text-gray-600">
                  <li className="mb-1">
                    <span className="font-medium">Date:</span> 
                    {isPublicDefenderEvent(selectedEvent) ? 'May 5, 2024' :
                     isVABenefitsEvent(selectedEvent) ? 'February 11, 2025' : 
                     isPoliceConfirmationEvent(selectedEvent) ? 'May 3, 2024' : 
                     isGunClipEvent(selectedEvent) ? 'December 20, 2024' : 
                     formatDate(selectedEvent.date)}
                  </li>
                  {selectedEvent.time && (
                    <li className="mb-1"><span className="font-medium">Time:</span> {formatTime(selectedEvent.time)}</li>
                  )}
                  <li className="mb-1"><span className="font-medium">Type:</span> {selectedEvent.type}</li>
                  <li className="mb-1"><span className="font-medium">ID:</span> {selectedEvent.id}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exhibit Detail Modal */}
      {selectedExhibit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={() => setSelectedExhibit(null)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-xl font-bold mb-2">{selectedExhibit.attachment.name}</h2>
            <p className="mb-2"><span className="font-semibold">Type:</span> {selectedExhibit.attachment.type}</p>
            {selectedExhibit.attachment.exhibitId && (
              <p className="mb-2"><span className="font-semibold">Exhibit ID:</span> {selectedExhibit.attachment.exhibitId}</p>
            )}
            <button
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={() => {
                openPdfInNewWindow(selectedExhibit.attachment.path);
              }}
            >
              Open PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to parse and format dates
const parseAndFormatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Handle dates like "April 29, 2023"
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
    
    // If that fails, try to parse specific formats
    const monthMap = {
      'january': '01', 'february': '02', 'march': '03', 'april': '04', 'may': '05', 'june': '06',
      'july': '07', 'august': '08', 'september': '09', 'october': '10', 'november': '11', 'december': '12'
    };
    
    // Try to match "Month Day, Year" format
    const monthDayYearPattern = /([a-z]+)\s+(\d{1,2}),?\s+(\d{4})/i;
    const match = dateString.match(monthDayYearPattern);
    
    if (match) {
      const month = monthMap[match[1].toLowerCase()];
      const day = String(parseInt(match[2])).padStart(2, '0');
      const year = match[3];
      if (month && day && year) {
        return `${year}-${month}-${day}`;
      }
    }
    
    // Return original if we can't parse it
    return dateString;
  } catch (e) {
    console.error(`Error parsing date: ${dateString}`, e);
    return dateString;
  }
};

// Helper function to format dates for display
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  } catch (e) {
    console.error(`Error formatting date: ${dateString}`, e);
    return dateString;
  }
};

// Helper function to format time strings
const formatTime = (timeString) => {
  // Format timestring like "15:30 PM" to "3:30 PM"
  if (!timeString) return '';
  try {
    // Extract hours and minutes
    const [hours, minutes] = timeString.split(':');
    const hoursNum = parseInt(hours, 10);
    let period = 'AM';
    let formattedHours = hoursNum;
    if (hoursNum > 12) {
      formattedHours = hoursNum - 12;
      period = 'PM';
    } else if (hoursNum === 12) {
      period = 'PM';
    } else if (hoursNum === 0) {
      formattedHours = 12;
    }
    
    return `${formattedHours}:${minutes.split(' ')[0]} ${period}`;
  } catch (error) {
    // If parsing fails, return the original string
    return timeString;
  }
};

// Helper function to get event type color
const getEventTypeColor = (type) => {
  const typeColors = {
    misconduct: 'bg-rose-500',
    alibi: 'bg-green-500',
    incident: 'bg-red-500',
    police: 'bg-blue-500',
    medical: 'bg-purple-500',
    legal: 'bg-indigo-500',
    evidence: 'bg-amber-500',
    consequence: 'bg-orange-500',
    exculpatory_evidence: 'bg-teal-500',
    procedural_issue: 'bg-yellow-500'
  };
  
  return typeColors[type] || 'bg-gray-500';
};

// Helper function to get event type badge color
const getEventTypeBadgeColor = (type) => {
  const typeBadgeColors = {
    misconduct: 'bg-rose-100 text-rose-800',
    alibi: 'bg-green-100 text-green-800',
    incident: 'bg-red-100 text-red-800',
    police: 'bg-blue-100 text-blue-800',
    medical: 'bg-purple-100 text-purple-800',
    legal: 'bg-indigo-100 text-indigo-800',
    evidence: 'bg-amber-100 text-amber-800',
    consequence: 'bg-orange-100 text-orange-800',
    exculpatory_evidence: 'bg-teal-100 text-teal-800',
    procedural_issue: 'bg-yellow-100 text-yellow-800'
  };

  return typeBadgeColors[type] || 'bg-gray-100 text-gray-800';
};

export default TimelinePage;